(() => {
  'use strict';

  const root = document.getElementById('PoppyAssistant');
  if (!root || root.dataset.initialised === '1') return;
  root.dataset.initialised = '1';

  const VAT_GUIDANCE_URL = 'https://www.fledglings.org.uk/pages/vat-exemption-declaration-2025';
  const SUPPORT_EMAIL = 'enquiries@fledglings.org.uk';
  const SUPPORT_PHONE = '0203 319 9772';
  const SUPPORT_HOURS = 'Monday to Friday, 10am to 2.30pm';

  const endpoint = root.dataset.endpoint;
  const launcher = root.querySelector('.poppy-launcher');
  const panel = root.querySelector('.poppy-panel');
  const close = root.querySelector('.poppy-close');
  const form = root.querySelector('.poppy-form');
  const input = root.querySelector('textarea');
  const send = form.querySelector('button');
  const messages = root.querySelector('.poppy-messages');
  const quickActions = root.querySelector('.poppy-quick-actions');
  const conversationId = sessionStorage.getItem('poppy_conversation_id') || crypto.randomUUID();
  sessionStorage.setItem('poppy_conversation_id', conversationId);

  const starterSuggestions = [
    {label: 'Where’s my order?', message: 'Where is my order?'},
    {label: 'Find swimsuit age 7', message: 'Can you help me find a swimsuit in age 7?'},
    {label: 'VAT relief', message: 'How does VAT relief work and might I qualify?'}
  ];

  const setOpen = (open) => {
    panel.hidden = !open;
    launcher.setAttribute('aria-expanded', String(open));
    if (open) setTimeout(() => input.focus(), 30);
  };

  const addLinkedText = (container, text) => {
    const pattern = /(https?:\/\/[^\s<]+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|0203\s*319\s*9772)/gi;
    let lastIndex = 0;

    for (const match of text.matchAll(pattern)) {
      if (match.index > lastIndex) {
        container.append(document.createTextNode(text.slice(lastIndex, match.index)));
      }

      const value = match[0];
      const link = document.createElement('a');
      link.textContent = value;

      if (/^https?:\/\//i.test(value)) {
        link.href = value.replace(/[),.;!?]+$/, '');
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      } else if (value.includes('@')) {
        link.href = `mailto:${value}`;
      } else {
        link.href = `tel:${value.replace(/\s+/g, '')}`;
      }

      container.append(link);
      lastIndex = match.index + value.length;
    }

    if (lastIndex < text.length) {
      container.append(document.createTextNode(text.slice(lastIndex)));
    }
  };


  const scrollLatestReplyToTop = (messageElement) => {
    if (!messageElement) return;

    requestAnimationFrame(() => {
      const container = messageElement.parentElement;
      if (!container) return;

      const widget = container.closest('.poppy-assistant, [data-poppy-assistant], .poppy-panel, .poppy-chat');
      const header = widget
        ? widget.querySelector('.poppy-header, .poppy-assistant__header, [data-poppy-header]')
        : null;

      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const topPadding = 16;

      container.scrollTop = Math.max(
        0,
        messageElement.offsetTop - headerHeight - topPadding
      );
    });
  };

  const addMessage = (text, type = 'assistant') => {
    const div = document.createElement('div');
    div.className = `poppy-message poppy-message--${type}`;

    if (type === 'assistant') addLinkedText(div, String(text));
    else div.textContent = text;

    messages.appendChild(div);
    if (div.classList.contains('poppy-message--assistant') || div.classList.contains('assistant') || div.dataset.poppyRole === 'assistant') scrollLatestReplyToTop(div);
    requestAnimationFrame(() => {
      const latestReply = messages.querySelector('.poppy-message--assistant:last-of-type, .poppy-message.assistant:last-of-type, [data-poppy-role="assistant"]:last-of-type');
      if (latestReply) {
        const widget = messages.closest('.poppy-assistant, [data-poppy-assistant], .poppy-panel, .poppy-chat');
        const header = widget
          ? widget.querySelector('.poppy-header, .poppy-assistant__header, [data-poppy-header]')
          : null;
        const headerHeight = header ? header.getBoundingClientRect().height : 0;
        messages.scrollTop = Math.max(0, latestReply.offsetTop - headerHeight - 16);
      }
    });
    return div;
  };

  const renderSuggestions = (suggestions = []) => {
    quickActions.innerHTML = '';

    suggestions.slice(0, 5).forEach((suggestion) => {
      if (!suggestion || !suggestion.label) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = suggestion.label;

      if (suggestion.url) {
        button.dataset.poppyUrl = suggestion.url;
      } else if (suggestion.message) {
        button.dataset.poppyQuestion = suggestion.message;
      }

      quickActions.appendChild(button);
    });

    quickActions.hidden = quickActions.children.length === 0;
  };

  const isVatQuestion = (text) => {
    return /\b(vat|tax\s*relief|tax\s*exempt|tax\s*exemption|vat\s*relief|vat\s*exempt|claim\s*tax|claim\s*vat|zero\s*rated)\b/i.test(text);
  };

  const inferSuggestions = (reply, question) => {
    const combined = `${question}\n${reply}`;

    if (/^\s*(?:thanks|thank you|many thanks|cheers|that(?:’|')s helpful|very helpful|perfect|great,? thanks|thanks very much)\s*[.!]*\s*$/i.test(question || '')) {
      return [];
    }

    if (isVatQuestion(combined)) {
      return [
        {label: 'Read VAT relief guidance', url: VAT_GUIDANCE_URL},
        {label: 'Ask another question', message: 'I have another question'}
      ];
    }

    if (/(external retailers?|competitor|brand websites?|another retailer|marketplace)/i.test(reply)) {
      return [
        {label: 'Show the Fledglings range', message: 'Show me the matching products available from Fledglings'},
        {label: 'Check a size', message: 'I would like to check a product size and availability'},
        {label: 'Contact Fledglings', message: 'How can I contact Fledglings?'}
      ];
    }

    const cleanedReply = reply
      .replace(/https?:\/\/\S+/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const yesNoQuestionPattern =
      /(?:would you like|do you want|shall i|may i|should i|can i|would it help if i|would you prefer)\b[^?]*\?/gi;

    const yesNoQuestions = cleanedReply.match(yesNoQuestionPattern) || [];
    const finalQuestion = yesNoQuestions.length
      ? yesNoQuestions[yesNoQuestions.length - 1].trim()
      : '';

    const asksExplicitYesNo =
      /\b(?:yes or no)\b/i.test(finalQuestion) ||
      /^(?:would you like|do you want|shall i|may i|should i|can i|would it help if i|would you prefer)\b/i.test(finalQuestion);

    if (asksExplicitYesNo && !/\bwhat would you like\b/i.test(finalQuestion)) {
      return [
        {label: 'Yes', message: 'Yes'},
        {label: 'No', message: 'No'}
      ];
    }

    if (/order number/i.test(reply) && /(email|postcode|post code)/i.test(reply)) {
      return [
        {label: 'Enter order details', message: 'My order number is '},
        {label: 'Contact Fledglings', message: 'How can I contact Fledglings?'}
      ];
    }

    if (/tracking/i.test(reply)) {
      const urlMatch = reply.match(/https?:\/\/[^\s<]+/i);
      const suggestions = [];
      if (urlMatch) suggestions.push({label: 'Open tracking', url: urlMatch[0].replace(/[),.;!?]+$/, '')});
      suggestions.push({label: 'Check another order', message: 'Check another order'});
      suggestions.push({label: 'Report a delivery problem', message: 'I need help with a delivery problem'});
      return suggestions;
    }

    if (/\b(school|nursery|college|local authority|nhs|healthcare|health care|hospital|clinic|care home|charity|occupational therapist|organisation|organization|professional)\b/i.test(combined)) {
      return [
        {label: 'How to get a quote', message: 'How do I create an online quote?'},
        {label: 'Ask another question', message: 'I have another question'}
      ];
    }

    if (/(phone|call us|contact fledglings|email us)/i.test(reply)) {
      return [
        {label: 'Email Fledglings', url: `mailto:${SUPPORT_EMAIL}`},
        {label: 'Call Fledglings', url: `tel:${SUPPORT_PHONE.replace(/\s+/g, '')}`}
      ];
    }

    return [
      {label: 'Ask another question', message: 'I have another question'},
      {label: 'Start again', message: 'Start again'}
    ];
  };


  const ask = async (question) => {
    const text = question.trim();
    if (!text) return;


    addMessage(text, 'user');
    renderSuggestions([]);
    input.value = '';
    input.style.height = 'auto';
    input.disabled = true;
    send.disabled = true;
    const waiting = addMessage('Poppy is checking…', 'assistant');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'same-origin',
        body: JSON.stringify({
          message: text,
          conversation_id: conversationId,
          context: {
            shop: root.dataset.shop || '',
            page_url: root.dataset.pageUrl || location.href,
            page_title: root.dataset.pageTitle || document.title,
            product_handle: root.dataset.productHandle || '',
            variant_id: (() => {
              const field = document.querySelector(
                'form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"]'
              );
              return field ? String(field.value || '') : '';
            })(),
            variant_title: (() => {
              const selected = document.querySelector(
                'form[action*="/cart/add"] select[name="id"] option:checked'
              );
              return selected ? String(selected.textContent || '').trim() : '';
            })(),
            vat_guidance_url: VAT_GUIDANCE_URL,
            support_email: SUPPORT_EMAIL,
            support_phone: SUPPORT_PHONE,
            support_hours: SUPPORT_HOURS
          }
        })
      });

      const data = await response.json().catch(() => ({}));
      waiting.remove();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Poppy is unavailable right now.');
      }

      const reply = data.reply || 'Sorry, I could not find an answer.';
      addMessage(reply, 'assistant');
      renderSuggestions(
        Array.isArray(data.suggestions) && data.suggestions.length
          ? data.suggestions
          : inferSuggestions(reply, text)
      );
    } catch (error) {
      waiting.remove();
      addMessage(error.message || 'Poppy is unavailable right now. Please contact Fledglings.', 'error');
      renderSuggestions([
        {label: 'Email Fledglings', url: `mailto:${SUPPORT_EMAIL}`},
        {label: 'Call Fledglings', url: `tel:${SUPPORT_PHONE.replace(/\s+/g, '')}`}
      ]);
    } finally {
      input.disabled = false;
      send.disabled = false;
      input.focus();
    }
  };

  launcher.addEventListener('click', () => setOpen(panel.hidden));
  close.addEventListener('click', () => setOpen(false));
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    ask(input.value);
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      form.requestSubmit();
    }
  });

  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 110)}px`;
  });

  quickActions.addEventListener('click', (event) => {
    const button = event.target.closest('button');
    if (!button) return;

    if (button.dataset.poppyUrl) {
      const url = button.dataset.poppyUrl;
      if (/^mailto:|^tel:/i.test(url)) window.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (button.dataset.poppyQuestion) ask(button.dataset.poppyQuestion);
  });

  renderSuggestions(starterSuggestions);
})();
